/**
 * Message Marquee Component for Re:Nav-96
 *
 * Displays the latest messages from the community in a scrolling marquee format.
 * Fetches messages from the API and continuously scrolls them for visibility.
 *
 * Features:
 * - Auto-scrolling message display
 * - Shows author name and message preview
 * - Links to message boards
 * - Graceful loading and error states
 * - Pause on hover
 *
 * @example
 * ```tsx
 * <MessageMarquee />
 * <MessageMarquee limit={10} />
 * ```
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { messagesApi } from '@/services/api';
import type { Message } from '@/types';
import './MessageMarquee.css';

/**
 * Props for the MessageMarquee component
 */
interface MessageMarqueeProps {
  /** Number of messages to display */
  limit?: number;
  /** Optional CSS class name for container */
  className?: string;
}

/**
 * Format relative time for message display
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Truncate message content for preview
 */
function truncateMessage(content: string, maxLength: number = 80): string {
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength).trim() + '...';
}

/**
 * Individual message item in the marquee
 */
interface MessageItemProps {
  message: Message;
}

function MessageItem({ message }: MessageItemProps) {
  const initials = message.authorName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div className="marquee-message">
      {/* Author avatar */}
      <div className="marquee-avatar">
        {message.authorAvatarUrl ? (
          <img
            src={message.authorAvatarUrl}
            alt={message.authorName}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-xs font-semibold text-primary">{initials}</span>
        )}
      </div>

      {/* Message content */}
      <div className="marquee-content">
        <span className="marquee-author">{message.authorName}</span>
        <span className="marquee-separator">·</span>
        <span className="marquee-text">{truncateMessage(message.content)}</span>
        <span className="marquee-time">{formatRelativeTime(message.createdAt)}</span>
      </div>
    </div>
  );
}

/**
 * Loading skeleton for the marquee
 */
function MarqueeLoadingSkeleton() {
  return (
    <div className="marquee-loading">
      <div className="marquee-loading-item" />
      <div className="marquee-loading-item" />
      <div className="marquee-loading-item" />
    </div>
  );
}

/**
 * Empty state when no messages are available
 */
function MarqueeEmptyState() {
  return (
    <div className="marquee-empty">
      <svg
        className="w-5 h-5 text-white/30 mr-2"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
      <span className="text-white/50 text-sm">
        No messages yet. Be the first to post!
      </span>
    </div>
  );
}

/**
 * MessageMarquee Component
 *
 * Displays a scrolling marquee of the latest community messages.
 * Automatically fetches messages and scrolls them horizontally.
 *
 * @param props - Component props
 * @returns The message marquee component
 *
 * @example
 * // Basic usage
 * <MessageMarquee />
 *
 * // With custom limit
 * <MessageMarquee limit={15} />
 */
export function MessageMarquee({
  limit = 10,
  className = '',
}: MessageMarqueeProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const marqueeRef = useRef<HTMLDivElement>(null);

  // Fetch latest messages
  useEffect(() => {
    let mounted = true;

    async function fetchMessages() {
      try {
        setIsLoading(true);
        setError(null);
        const data = await messagesApi.getLatest({ limit });
        if (mounted) {
          setMessages(data);
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to load messages');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    fetchMessages();

    // Refresh messages every 2 minutes
    const intervalId = setInterval(fetchMessages, 120000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [limit]);

  // Handle hover pause
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  return (
    <div className={`marquee-container card ${className}`}>
      {/* Header */}
      <div className="marquee-header">
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <h3 className="text-lg font-display font-semibold text-white">
            Latest Messages
          </h3>
        </div>
        <Link
          to="/member/groups"
          className="text-primary text-sm font-medium hover:text-primary-light transition-colors inline-flex items-center gap-1"
        >
          View All
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      {/* Marquee content */}
      <div
        className="marquee-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={marqueeRef}
      >
        {isLoading ? (
          <MarqueeLoadingSkeleton />
        ) : error ? (
          <div className="marquee-error">
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        ) : messages.length === 0 ? (
          <MarqueeEmptyState />
        ) : (
          <div className={`marquee-track ${isPaused ? 'paused' : ''}`}>
            {/* Duplicate messages for seamless loop */}
            <div className="marquee-items">
              {messages.map((message, index) => (
                <MessageItem key={`${message.rowKey}-${index}`} message={message} />
              ))}
            </div>
            <div className="marquee-items" aria-hidden="true">
              {messages.map((message, index) => (
                <MessageItem key={`${message.rowKey}-dup-${index}`} message={message} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Live indicator */}
      {!isLoading && !error && messages.length > 0 && (
        <div className="marquee-live">
          <span className="marquee-live-dot" />
          <span className="text-white/40 text-xs">Live updates</span>
        </div>
      )}
    </div>
  );
}

export default MessageMarquee;
